import type { PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "reducers/store"

import { createSlice } from "@reduxjs/toolkit"
import { signOut } from "reducers/profileSlice"
import { getDefaultWorkCycleDateRange } from "utils/dateRange"

export type AttendanceViewMode = "common" | "personal"
export type RequestLeaveViewMode = "calendar" | "list"

type DataViewFilterPreferences = {
  attendees: Attendee[]
  endDate: string
  filtersExpanded: boolean
  startDate: string
}

type AttendancePreferences = {
  mode: AttendanceViewMode
} & DataViewFilterPreferences

type RequestLeavePreferences = {
  mode: RequestLeaveViewMode
} & DataViewFilterPreferences

type RequestStatisticsPreferences = DataViewFilterPreferences

type DataViewPreferencesState = {
  attendance: AttendancePreferences
  requestLeave: RequestLeavePreferences
  requestStatistics: RequestStatisticsPreferences
}

const defaultDateRange = getDefaultWorkCycleDateRange()
const defaultStartDate = defaultDateRange.startDate.toFormat("yyyy-MM-dd")
const defaultEndDate = defaultDateRange.endDate.toFormat("yyyy-MM-dd")

const initialState: DataViewPreferencesState = {
  attendance: {
    attendees: [],
    endDate: defaultEndDate,
    filtersExpanded: false,
    mode: "personal",
    startDate: defaultStartDate,
  },
  requestLeave: {
    attendees: [],
    endDate: defaultEndDate,
    filtersExpanded: false,
    mode: "list",
    startDate: defaultStartDate,
  },
  requestStatistics: {
    attendees: [],
    endDate: defaultEndDate,
    filtersExpanded: false,
    startDate: defaultStartDate,
  },
}

export const dataViewPreferencesSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(signOut, () => initialState)
  },
  initialState,
  name: "dataViewPreferences",
  reducers: {
    updateAttendancePreferences: (state, { payload }: PayloadAction<Partial<AttendancePreferences>>) => {
      Object.assign(state.attendance, payload)
    },
    updateRequestLeavePreferences: (state, { payload }: PayloadAction<Partial<RequestLeavePreferences>>) => {
      Object.assign(state.requestLeave, payload)
    },
    updateRequestStatisticsPreferences: (state, { payload }: PayloadAction<Partial<RequestStatisticsPreferences>>) => {
      if (!state.requestStatistics) {
        state.requestStatistics = { ...initialState.requestStatistics, attendees: [] }
      }
      Object.assign(state.requestStatistics, payload)
    },
  },
})

export const { updateAttendancePreferences, updateRequestLeavePreferences, updateRequestStatisticsPreferences } =
  dataViewPreferencesSlice.actions

export const dataViewPreferencesSelector = ({ dataViewPreferences }: RootState) => dataViewPreferences

export const requestStatisticsPreferencesSelector = ({ dataViewPreferences }: RootState) =>
  dataViewPreferences.requestStatistics ?? initialState.requestStatistics
